import { useSignal, batch } from "@preact/signals-react";
import { getLocale, setupLocalState } from "../../library/dataService";
import { INGFinAppProps } from "../../library/NGFieldExtensions";
import { getClassName, getTestId, getsxObject, escapeAttribute, getLanguageCode } from "../../library/utils";
import { useFinApp } from "../../library/finapp";
import { executeAction } from "../../library/actions";
import { Box } from "@mui/material";
import { publishUserAction } from "../../library/analytics";
import { log } from "../../library/logger";

const tag = "NGFinApp";

const alertClickAction = (configId: string, actionName: "onLinkAlert" | "onLinkClick") => ({
  Trigger: "onFinAppAction",
  CommandSet: {
    FirstCommandId: "1",
    ExecuteCommandsInParallel: false,
    Commands: [
      {
        Id: "1",
        Instruction: {
          Name: "TriggerAction",
        },
        Parameters: [
          {
            Name: "Trigger",
            Value: actionName,
          },
          {
            Name: "TargetId",
            Value: configId,
          },
        ],
      },
    ],
  },
});

export default function NGFinApp({ config, context }: INGFinAppProps) {
  const local = setupLocalState(
    config,
    {
      Visible: useSignal(config.Visible ?? true),
      Classes: useSignal(config.Classes ?? ""),
      Style: useSignal(config.Style ?? {}),
      Type: useSignal(config.Type ?? "news"),
      Symbol: useSignal(config.Symbol),
      Exchange: useSignal(config.Exchange),
      AppConfigId: useSignal(config.AppConfigId),
      Elements: useSignal(config.Elements),
      XID: useSignal(config.XID),
      XIDs: useSignal(config.XIDs),
      HideElements: useSignal(config.HideElements),
      HideLoader: useSignal(config.HideLoader ?? false),
      View: useSignal(config.View),
      EventCount: useSignal(config.EventCount),
      ModelId: useSignal(config.ModelId),
      LimitColumns: useSignal(config.LimitColumns),
      ShowLinks: useSignal(config.ShowLinks),
      RankingType: useSignal(config.RankingType),
      ShouldShowConfiguration: useSignal(config.ShouldShowConfiguration ?? false),
      UserPrefs: useSignal(config.UserPrefs ?? {}),
      Indices: useSignal(config.Indices ?? []),
      MarketIdentifier: useSignal(config.MarketIdentifier ?? undefined),
      ShowInternational:useSignal(config.ShowInternational),
      SelectedCountry: useSignal(config.SelectedCountry),
      ComparisonList: useSignal(config.ComparisonList ?? []),
    },
    context
  );

  const onSearch = (data) => {
    batch(() => {
      local.Symbol.value = data.symbol;
      local.Exchange.value = data.exchange.code;
      local.XID.value = data.xid.code;
    });
  };

  const onLinkSymbolClick = (data: object, componentId: string) => {
    log.info(tag, `onLinkClick for component with Id = ${componentId} with data:`, { data });
    executeAction(alertClickAction(componentId, "onLinkClick"), null, data, context);
  };

  const onLinkAlertClick = (data, componentId) => {
    log.info(tag, `onLinkAlert for component with Id = ${componentId} with data:`, { data });
    executeAction(alertClickAction(componentId, "onLinkAlert"), null, data, context);
  };

  const onAnalytics = (data, e) => {
    publishUserAction(config, context, data.EventName, e, data);
  };

  useFinApp(onSearch, onLinkSymbolClick, onLinkAlertClick, onAnalytics);

  const ComponentTag = `fwc-${local.Type.value}` || "div";

  return (
    <>
      {local.Visible.value && (
        <Box
          data-testid={getTestId(config)}
          data-type={config.__typename}
          sx={getsxObject(local.Style.value)}
          className={getClassName(local.Classes)}
        >
          <ComponentTag
            className="fwc-app"
            xid={escapeAttribute(local.XID.value ?? "")}
            xids={escapeAttribute(local.XIDs.value ?? "")}
            symbol={escapeAttribute(local.Symbol.value ?? "")}
            userPrefs={escapeAttribute(encodeURI(JSON.stringify(local.UserPrefs.value)))}
            exchange={escapeAttribute(local.Exchange.value ?? "")}
            hideElements={escapeAttribute(local.HideElements.value ?? "")}
            hideLoader={local.HideLoader.value}
            indices={escapeAttribute(encodeURI(JSON.stringify(local.Indices.value)))}
            marketIdentifier={escapeAttribute(local.MarketIdentifier.value ?? "")}
            lang={getLanguageCode(getLocale())}
            criteria={escapeAttribute(
              encodeURI(
                JSON.stringify({
                  appConfigId: local.AppConfigId.value,
                  elements: local.Elements.value,
                  xid: local.XID.value,
                  xids: local.XIDs.value,
                })
              )
            )}
            {...(local.ModelId.value ? { modelId: escapeAttribute(local.ModelId.value) } : {})}
            {...(local.View.value ? { view: escapeAttribute(local.View.value) } : {})}
            {...(local.EventCount.value ? { eventcount: escapeAttribute(local.EventCount.value) } : {})}
            {...(local.LimitColumns.value == true ? { limitColumns: true } : {})}
            {...(local.ShowLinks.value == true ? { showLinks: true } : {})}
            {...(local.RankingType.value ? { rankingType: escapeAttribute(local.RankingType.value) } : {})}
            {...(local.ShouldShowConfiguration.value ? { shouldShowConfiguration: local.ShouldShowConfiguration.value } : {})}
            {...(local.ShowInternational.value ? { showInternational: local.ShowInternational.value } : {})}
            {...(local.SelectedCountry.value ? { selectedCountry: escapeAttribute(local.SelectedCountry.value) } : {})}
            {...(local.ComparisonList.value ? { comparisonList: escapeAttribute(encodeURI(JSON.stringify(local.ComparisonList.value)))}: {})}
          ></ComponentTag>
        </Box>
      )}
    </>
  );
}
